import React, { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";

interface SurveyVersionListItem {
  id: number;
  versionNumber: number;
  surveyName: string;
  changedBy: string;
  changeType: string;
  savedAt: string; // ISO
}

interface SurveyVersionDetail extends SurveyVersionListItem {
  snapshot: any;
}

interface Props {
  surveyId: number;
  surveyName: string;
  onRestored(restoredSurvey: any): void;
  onClose(): void;
}

function formatSavedAt(savedAt: string): { primary: string; secondary: string } {
  const date = new Date(savedAt);
  if (isNaN(date.getTime())) return { primary: savedAt, secondary: "" };
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yestStart = new Date(todayStart);
  yestStart.setDate(yestStart.getDate() - 1);
  const versionDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const timeStr = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (versionDay.getTime() === todayStart.getTime()) return { primary: "Today", secondary: timeStr };
  if (versionDay.getTime() === yestStart.getTime()) return { primary: "Yesterday", secondary: timeStr };
  return {
    primary: date.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }),
    secondary: timeStr
  };
}

function shortEmail(email: string): string {
  if (!email) return "Unknown";
  return email.split("@")[0];
}

function ChangeTypeBadge({ type }: { type: string }) {
  const map: Record<string, string> = {
    Updated: "bg-blue-50 text-blue-700 border border-blue-200",
    Created: "bg-green-50 text-green-700 border border-green-200",
    PreRestore: "bg-slate-100 text-slate-500 border border-slate-200",
    Restored: "bg-amber-50 text-amber-700 border border-amber-200"
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${map[type] ?? map.Updated}`}>
      {type}
    </span>
  );
}

export default function SurveyVersionHistoryDrawer({ surveyId, surveyName, onRestored, onClose }: Props) {
  const [versions, setVersions] = useState<SurveyVersionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actingVersion, setActingVersion] = useState<number | null>(null);
  const [previewVersion, setPreviewVersion] = useState<SurveyVersionDetail | null>(null);
  const api = useApi();

  useEffect(() => {
    loadVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  async function loadVersions() {
    setLoading(true);
    setError("");
    try {
      const response = await api.messaging.get(`Surveys/${surveyId}/versions`);
      if (!response.ok) {
        const body = await response.text();
        setError(`Failed to load versions (HTTP ${response.status}): ${body || "no body"}`);
        return;
      }
      const data = await response.json();
      setVersions(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(`Failed to load version history: ${e?.message ?? String(e)}`);
    } finally {
      setLoading(false);
    }
  }

  async function handlePreview(versionNumber: number) {
    setActingVersion(versionNumber);
    setError("");
    try {
      const response = await api.messaging.get(`Surveys/${surveyId}/versions/${versionNumber}`);
      if (!response.ok) {
        const body = await response.text();
        setError(`Failed to load version (HTTP ${response.status}): ${body || "no body"}`);
        return;
      }
      const data = await response.json();
      setPreviewVersion(data);
    } catch (e: any) {
      setError(`Failed to load version: ${e?.message ?? String(e)}`);
    } finally {
      setActingVersion(null);
    }
  }

  async function handleRestore(versionNumber: number) {
    if (!window.confirm(
      `Restore version ${versionNumber}?\n\n` +
      `The current state of "${surveyName}" will be saved as a new version first, ` +
      `so this restore is reversible. The survey will then be overwritten with the ` +
      `selected snapshot.`
    )) return;

    setActingVersion(versionNumber);
    setError("");
    try {
      const response = await api.messaging.post(
        `Surveys/${surveyId}/versions/${versionNumber}/restore`,
        {}
      );
      if (!response.ok) {
        const body = await response.text();
        setError(`Failed to restore (HTTP ${response.status}): ${body || "no body"}`);
        return;
      }
      const restored = await response.json();
      onRestored(restored);
      // refresh list to show the new PreRestore + Restored entries
      await loadVersions();
      setPreviewVersion(null);
    } catch (e: any) {
      setError(`Failed to restore: ${e?.message ?? String(e)}`);
    } finally {
      setActingVersion(null);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/30 z-40 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Survey version history"
      onClick={onClose}
    >
      <div
        className="h-full w-full max-w-md bg-white border-l border-slate-200 shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50 flex-shrink-0">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Version history</h3>
            <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[260px]">
              {surveyName} — last 50 saves
            </p>
          </div>
          <button
            aria-label="Close version history"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded focus:outline-none focus:ring-2 focus:ring-primary-hover"
          >
            ✕
          </button>
        </div>

        {/* Inline error banner */}
        {error && (
          <div className="bg-red-50 border-b border-red-200 px-4 py-2 text-xs text-red-700">
            {error}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-6">
              <LoadingSpinner inline />
              <div className="text-center text-xs text-slate-400 mt-2">Loading versions…</div>
            </div>
          )}

          {!loading && versions.length === 0 && !error && (
            <div className="p-6 text-sm text-slate-500 text-center">
              No version history yet.
              <br />
              <span className="text-xs text-slate-400 mt-1 block">
                A version is saved every time you save changes to this survey.
              </span>
            </div>
          )}

          {!loading && versions.map((v, idx) => {
            const { primary, secondary } = formatSavedAt(v.savedAt);
            const isLatest = idx === 0;
            const isActing = actingVersion === v.versionNumber;
            return (
              <div
                key={v.id}
                className="px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center pt-1 flex-shrink-0">
                    <div className={`w-2.5 h-2.5 rounded-full ${isLatest ? "bg-primary" : "bg-slate-300"}`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-sm font-semibold text-slate-800">v{v.versionNumber}</span>
                      <span className="text-sm text-slate-500">{primary}</span>
                      <span className="text-sm text-slate-400">{secondary}</span>
                      <ChangeTypeBadge type={v.changeType ?? "Updated"} />
                      {isLatest && (
                        <span className="text-xs font-medium bg-primary-bg text-primary-active border border-cornflower-blue-200 px-2 py-0.5 rounded-full">
                          Latest
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 space-y-0.5">
                      <div>by {shortEmail(v.changedBy ?? "")}</div>
                      {v.surveyName && v.surveyName !== surveyName && (
                        <div>Was named: {v.surveyName}</div>
                      )}
                    </div>

                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handlePreview(v.versionNumber)}
                        disabled={actingVersion !== null}
                        className="text-xs font-medium px-2.5 py-1 rounded-md border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 disabled:opacity-50"
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRestore(v.versionNumber)}
                        disabled={actingVersion !== null}
                        className={`text-xs font-medium px-2.5 py-1 rounded-md border transition-colors ${
                          isActing
                            ? "bg-slate-100 text-slate-400 border-slate-200 cursor-wait"
                            : "bg-white text-primary border-cornflower-blue-200 hover:bg-primary-bg hover:border-cornflower-blue-300"
                        } disabled:opacity-50`}
                      >
                        {isActing ? "Working…" : "Restore"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Preview overlay */}
        {previewVersion && (
          <div
            className="absolute inset-0 bg-white border-l border-slate-200 flex flex-col"
            role="dialog"
            aria-label="Version preview"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50 flex-shrink-0">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">
                  Preview v{previewVersion.versionNumber}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Read-only snapshot — Restore to apply.
                </p>
              </div>
              <button
                aria-label="Close preview"
                onClick={() => setPreviewVersion(null)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 text-xs">
              {previewVersion.snapshot ? (
                <>
                  <div className="mb-3">
                    <div className="text-slate-500 mb-0.5">Name</div>
                    <div className="font-semibold text-slate-800">{previewVersion.snapshot.name}</div>
                  </div>
                  {previewVersion.snapshot.formHeading && (
                    <div className="mb-3">
                      <div className="text-slate-500 mb-0.5">Form heading</div>
                      <div className="text-slate-700">{previewVersion.snapshot.formHeading}</div>
                    </div>
                  )}
                  <div className="mb-3">
                    <div className="text-slate-500 mb-1">
                      Questions ({previewVersion.snapshot.questions?.length ?? 0})
                    </div>
                    <ol className="list-decimal pl-5 space-y-1">
                      {(previewVersion.snapshot.questions ?? []).map((q: any, i: number) => (
                        <li key={i} className="text-slate-700">
                          <span className="text-slate-400">[{q.type}]</span>{" "}
                          {q.text || <em className="text-slate-300">(no text)</em>}
                          {q.required && <span className="text-red-500"> *</span>}
                        </li>
                      ))}
                    </ol>
                  </div>
                </>
              ) : (
                <div className="text-slate-400 italic">Snapshot data unavailable.</div>
              )}
            </div>
            <div className="border-t border-slate-200 px-4 py-3 flex items-center gap-2 justify-end flex-shrink-0">
              <button
                type="button"
                onClick={() => setPreviewVersion(null)}
                className="text-xs font-medium px-3 py-1.5 rounded-md border border-slate-200 text-slate-600 bg-white hover:bg-slate-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => handleRestore(previewVersion.versionNumber)}
                disabled={actingVersion !== null}
                className="text-xs font-medium px-3 py-1.5 rounded-md border bg-white text-primary border-cornflower-blue-200 hover:bg-primary-bg disabled:opacity-50"
              >
                Restore this version
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
